/**
 * Analytics Service
 * API calls for dashboards, metrics, and reports
 */

import { createApiClient } from '@shared/auth';
import apiConfig from '../config/apiConfig';
import { extractList, extractErrorMessage } from '../utils/responseHelpers';

const baseURL = apiConfig.getAnalyticsServiceUrl();

const client = createApiClient(baseURL);

/**
 * Get dashboard metrics summary
 */
export const getDashboardMetrics = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/metrics/summary', { params: dateRange });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch metrics.') };
  }
};

/**
 * Get overview dashboard (KPIs, trends, top agents)
 */
export const getOverviewDashboard = async (params) => {
  try {
    const response = await client.get('/api/v1/dashboards/overview', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch overview.') };
  }
};

/**
 * Get executive dashboard KPIs
 */
export const getExecutiveKPIs = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/dashboards/executive', { params: dateRange });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch KPIs.') };
  }
};

/**
 * Get metrics with filters
 */
export const getMetrics = async (params) => {
  try {
    const response = await client.get('/api/v1/metrics', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch metrics.') };
  }
};

/**
 * Get reports list
 */
export const getReports = async (params) => {
  try {
    const response = await client.get('/api/v1/reports', { params });
    return { success: true, data: extractList(response.data, 'reports', 'items'), total: response.data?.total ?? 0 };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch reports.') };
  }
};

/**
 * Create report definition
 */
export const createReport = async (reportData) => {
  try {
    const response = await client.post('/api/v1/reports', reportData);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to create report.') };
  }
};

/**
 * Execute a report
 */
export const executeReport = async (reportId) => {
  try {
    const response = await client.post(`/api/v1/reports/${reportId}/execute`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to execute report.') };
  }
};

/**
 * Get dashboard widgets
 */
export const getWidgets = async (dashboardType) => {
  try {
    const response = await client.get('/api/v1/widgets', { params: { dashboard_type: dashboardType } });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch widgets.') };
  }
};

/**
 * Get agent leaderboard
 */
export const getAgentLeaderboard = async (params) => {
  try {
    const response = await client.get('/api/v1/dashboards/agents', { params });
    return {
      success: true,
      data: extractList(response.data, 'agents', 'items'),
      summary: response.data.summary || null,
    };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch agents.') };
  }
};

/**
 * Get individual agent performance
 */
export const getAgentPerformance = async (agentId, params) => {
  try {
    const response = await client.get(`/api/v1/dashboards/agent/${agentId}`, { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch agent data.') };
  }
};

/**
 * Export a report
 */
export const exportReport = async (reportId, format, dateRange) => {
  try {
    const body = { format };
    if (dateRange?.from) body.from_date = dateRange.from;
    if (dateRange?.to) body.to_date = dateRange.to;
    const response = await client.post(`/api/v1/reports/${reportId}/export`, body);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to export report.') };
  }
};

/**
 * Delete a report definition
 */
export const deleteReport = async (reportId) => {
  try {
    await client.delete(`/api/v1/reports/${reportId}`);
    return { success: true };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to delete report.') };
  }
};

/**
 * Get export history
 */
export const getExports = async (params) => {
  try {
    const response = await client.get('/api/v1/exports', { params });
    return { success: true, data: extractList(response.data, 'exports', 'items'), total: response.data?.total ?? 0 };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch exports.') };
  }
};

/**
 * Download an export file (authenticated blob request)
 */
export const downloadExport = async (exportId) => {
  try {
    const response = await client.get(`/api/v1/exports/${exportId}/download`, { responseType: 'blob' });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to download export.') };
  }
};

/**
 * Get analytics settings
 */
export const getSettings = async () => {
  try {
    const response = await client.get('/api/v1/settings');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch settings.') };
  }
};

/**
 * Update analytics settings
 */
export const updateSettings = async (settingsData) => {
  try {
    const response = await client.put('/api/v1/settings', settingsData);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to save settings.') };
  }
};

/**
 * Get agent profiles (admin management)
 */
export const getAgentProfiles = async (params) => {
  try {
    const response = await client.get('/api/v1/agent-profiles', { params });
    return { success: true, data: extractList(response.data, 'profiles', 'items') };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch agent profiles.') };
  }
};

/**
 * Get the profiles of the given agents (at most 100 ids per call)
 */
export const getAgentProfilesByIds = async (agentIds) =>
  getAgentProfiles({ agent_ids: agentIds.join(','), page_size: 100 });

/**
 * Create agent profile
 */
export const createAgentProfile = async (data) => {
  try {
    const response = await client.post('/api/v1/agent-profiles', data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to create agent profile.') };
  }
};

/**
 * Update agent profile
 */
export const updateAgentProfile = async (id, data) => {
  try {
    const response = await client.put(`/api/v1/agent-profiles/${id}`, data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to update agent profile.') };
  }
};

/**
 * Delete agent profile
 */
export const deleteAgentProfile = async (id) => {
  try {
    await client.delete(`/api/v1/agent-profiles/${id}`);
    return { success: true };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to delete agent profile.') };
  }
};

/**
 * Get transcript for an interaction (served from analytics_service)
 */
export const getInteractionTranscript = async (interactionId) => {
  try {
    const response = await client.get(`/api/v1/interactions/${interactionId}/transcript`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch transcript.') };
  }
};

/**
 * Get pipeline status for a single interaction
 */
export const getPipelineStatus = async (callHistoryId) => {
  try {
    const response = await client.get(`/api/v1/interactions/${callHistoryId}/pipeline-status`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch pipeline status.') };
  }
};

/**
 * Retry a specific pipeline step for an interaction
 */
export const retryPipelineStep = async (callHistoryId, stepType) => {
  try {
    const response = await client.post(`/api/v1/interactions/${callHistoryId}/retry/${stepType}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to retry pipeline step.') };
  }
};

/**
 * Batch query pipeline summary for multiple interactions
 */
export const getPipelineSummary = async (interactionIds) => {
  try {
    const response = await client.get('/api/v1/pipeline-summary', {
      params: { interaction_ids: interactionIds.join(',') },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch pipeline summary.') };
  }
};

const analyticsServiceDefault = {
  getDashboardMetrics,
  getOverviewDashboard,
  getExecutiveKPIs,
  getMetrics,
  getReports,
  createReport,
  executeReport,
  getWidgets,
  getAgentLeaderboard,
  getAgentPerformance,
  exportReport,
  deleteReport,
  getExports,
  downloadExport,
  getSettings,
  updateSettings,
  getAgentProfiles,
  getAgentProfilesByIds,
  createAgentProfile,
  updateAgentProfile,
  deleteAgentProfile,
  getInteractionTranscript,
  getPipelineStatus,
  retryPipelineStep,
  getPipelineSummary,
};

export default analyticsServiceDefault;
